import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'

import { AmortizationSummary, RentBreakdown } from '@data/utils/amortization-table.util'

//* El tipo de la función de tablas, tomado del propio paquete. Va en posición de TIPO a propósito:
//* así se borra al compilar y `jspdf-autotable` sigue cargándose solo cuando alguien pide el estado
//* de cuenta, en vez de entrar al paquete principal.
type AutoTable = typeof import('jspdf-autotable').default
//* Y el tipo de una fila, para que las celdas con estilo se verifiquen de verdad: `fontStyle` y
//* `halign` no admiten cualquier cadena. Antes no se verificaba nada porque `doc` era `any`.
type RowInput = import('jspdf-autotable').RowInput

//* Medidas en mm sobre A4. Copian las del PDF de cotización (`api-rendafin/.../pdfQuotation.service.ts`)
//* para que los dos documentos se lean como de la misma casa.
const MARGIN = 10
const HEADER_HEIGHT = 18
const CONTINUATION_HEADER_HEIGHT = 14
//* Lo que el pie reserva abajo: ninguna tabla puede escribir debajo de esta franja.
const FOOTER_RESERVED = 22
const SECTION_TITLE_HEIGHT = 6

const CONTACT_LINE = 'Montes Urales 430, Col. Lomas de Chapultepec III sección, C.P. 11000, CDMX  |  55-26-28-98-86  |  contacto@rendafin.com'

/**
 * Estado de cuenta del arrendamiento en PDF, generado en el navegador.
 *
 * Mismo lenguaje visual que la cotización: banda oscura con el logo, tarjeta de cliente y unidad,
 * secciones con barra verde y tablas con encabezado oscuro. Cuando otro canal necesite el estado de
 * cuenta se centraliza en el API junto al de cotización (ADR-0005).
 */
@Injectable({
  providedIn: 'root',
})
export class PdfBillingStatementService {
  private logoPath = 'assets/media/images/logo/Rendafin.png'

  private colors = {
    primary: '#202835',
    success: '#00C781',
    danger: '#d21313',
    grey: '#6c757d',
    lightGrey: '#adb5bd',
    cardBg: '#ffffff',
    border: '#e2e8f0',
    headerBg: '#202835',
    highlightBg: '#ecfdf5',
    sectionBg: '#f1f5f9',
  }

  constructor(private http: HttpClient) {}

  /**
   * Generar Estado de Cuenta en PDF. `table` es lo leído de la tabla de amortización (B-085): renta vigente,
   * inicio y cobradas/anticipadas. `termData` es la foto del día en que se cotizó y solo aporta lo que no cambia.
   */
  async generateBillingStatement(leaseData: any, termData: any, table: AmortizationSummary): Promise<void> {
    //* jspdf-autotable 5 ya no registra `doc.autoTable`: se usa la forma de función. `doc.lastAutoTable`
    //* sí se sigue rellenando, que es de lo que cuelga la posición vertical de las secciones de abajo.
    const jsPDF = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF.default('p', 'mm', 'a4') as any
    const pageWidth = doc.internal.pageSize.getWidth()
    const logoBase64 = await this.loadLogo()
    const cutoff = new Date()

    let y = this.addHeader(doc, logoBase64, leaseData.folio, cutoff, pageWidth)
    y = this.addPartiesCard(doc, leaseData, y, pageWidth)
    y = this.addLeaseSection(doc, autoTable, leaseData, termData, table, y, pageWidth)
    y = this.addRentSection(doc, autoTable, termData, table.rent, y, pageWidth)
    y = this.addBillingSection(doc, autoTable, leaseData, table, y, pageWidth)
    this.addNotes(doc, table.rent, y)

    //* El encabezado de continuación y el pie se pintan al final: hasta aquí no se sabe cuántas páginas hay.
    this.decoratePages(doc, logoBase64, leaseData.folio, cutoff, pageWidth)

    doc.save(`Estado de Cuenta - ${leaseData.folio}.pdf`)
  }

  private async loadLogo(): Promise<string> {
    try {
      const blob = await firstValueFrom(this.http.get(this.logoPath, { responseType: 'blob' }))
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error('Error cargando logo:', error)
      return ''
    }
  }

  // ---------- Encabezados ----------

  private addHeader(doc: any, logoBase64: string, folio: string, cutoff: Date, pageWidth: number): number {
    doc.setFillColor(this.colors.headerBg)
    doc.rect(0, 0, pageWidth, HEADER_HEIGHT, 'F')
    //* 346 × 110 px: el alto sale del ancho para no deformar el logo.
    if (logoBase64) doc.addImage(logoBase64, 'PNG', 12, 3.6, 34, 10.8)

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(255, 255, 255)
    doc.text('ESTADO DE CUENTA', pageWidth - 15, 8, { align: 'right' })

    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.text(`No. ${folio || '-'}  |  Corte al ${this.formatDate(cutoff)}`, pageWidth - 15, 13, { align: 'right' })

    return HEADER_HEIGHT + 4
  }

  private addContinuationHeader(doc: any, logoBase64: string, folio: string, pageWidth: number): void {
    doc.setFillColor(this.colors.headerBg)
    doc.rect(0, 0, pageWidth, CONTINUATION_HEADER_HEIGHT, 'F')
    if (logoBase64) doc.addImage(logoBase64, 'PNG', 12, 2.5, 28, 8.9)

    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(255, 255, 255)
    doc.text('ESTADO DE CUENTA', pageWidth - 15, 6, { align: 'right' })

    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'normal')
    doc.text(`No. ${folio || '-'}`, pageWidth - 15, 11, { align: 'right' })
  }

  // ---------- Cliente / Unidad ----------

  private addPartiesCard(doc: any, leaseData: any, y: number, pageWidth: number): number {
    const cardWidth = pageWidth - MARGIN * 2
    const colWidth = (cardWidth - 4) / 2
    const cardHeight = 20
    const top = y + 7

    this.drawCard(doc, MARGIN, y, cardWidth, cardHeight)

    const client = leaseData.client || {}
    this.drawPartyColumn(doc, 'CLIENTE', client.name || '-', `Email: ${this.firstOf(client.email)}  |  Tel: ${this.firstOf(client.phone)}`, MARGIN + 4, top, colWidth - 8)

    doc.setDrawColor(this.colors.border)
    doc.setLineWidth(0.3)
    doc.line(MARGIN + colWidth + 2, y + 3, MARGIN + colWidth + 2, y + cardHeight - 3)

    const assetValue = leaseData.quotation?.assetsValue?.amount
    this.drawPartyColumn(doc, 'EQUIPO / UNIDAD', this.assetNames(leaseData), assetValue ? `Valor del activo: ${this.formatCurrency(assetValue)}` : '', MARGIN + colWidth + 8, top, colWidth - 8)

    return y + cardHeight + 5
  }

  private drawPartyColumn(doc: any, label: string, title: string, detail: string, x: number, y: number, maxWidth: number): void {
    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(this.colors.success)
    doc.text(label, x, y)

    doc.setFontSize(9)
    doc.setTextColor(this.colors.primary)
    doc.text(this.truncateText(doc, title, maxWidth), x, y + 4)

    doc.setFontSize(7)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(this.colors.grey)
    doc.text(this.truncateText(doc, detail, maxWidth), x, y + 8)
  }

  // ---------- Secciones ----------

  private addLeaseSection(doc: any, autoTable: AutoTable, leaseData: any, termData: any, table: AmortizationSummary, y: number, pageWidth: number): number {
    const quotation = leaseData.quotation || {}
    const commission = quotation.openingCommission
    const commissionText = commission?.percentage ? `${(commission.percentage * 100).toFixed(2)} %  ·  ${this.formatCurrency(commission.total)}` : this.formatCurrency(commission?.total)

    y = this.addSectionTitle(doc, 'INFORMACIÓN DEL ARRENDAMIENTO', y, pageWidth)

    const body: RowInput[] = [
      ['Inicio del arrendamiento', this.formatDate(table.startDate, true), 'Tipo de arrendamiento', quotation.leaseType || '-'],
      ['Plazo', termData?.term ? `${termData.term} meses` : '-', 'Rentas anticipadas', termData?.advancedRents?.number?.toString() ?? '0'],
      ['Tasa de interés', this.formatPercent(quotation.interestRate), 'Estatus', leaseData.status || '-'],
      ['Valor pagaré', this.formatCurrency(quotation.amountToFinance), 'Pago inicial', this.formatCurrency(termData?.downPayment?.total)],
      ['Comisión por apertura', commissionText, 'Gastos de ratificación', this.formatCurrency(quotation.ratificationExpenses)],
      ['Valor residual', this.formatCurrency(termData?.residuary?.total), 'Pago total del arrendamiento', this.formatCurrency(termData?.totalLeasePayment)],
    ]

    const labelWidth = (pageWidth - MARGIN * 2) * 0.22
    const valueWidth = (pageWidth - MARGIN * 2) * 0.28

    autoTable(doc, {
      ...this.tableBase(),
      startY: y,
      body,
      columnStyles: {
        0: { cellWidth: labelWidth, textColor: this.colors.grey },
        1: { cellWidth: valueWidth, fontStyle: 'bold' },
        2: { cellWidth: labelWidth, textColor: this.colors.grey },
        3: { cellWidth: valueWidth, fontStyle: 'bold' },
      },
      didParseCell: (data: any) => {
        //* El total del contrato es la cifra que el cliente busca primero: va en verde, como en la cotización.
        if (data.section === 'body' && data.row.index === body.length - 1 && data.column.index === 3) data.cell.styles.textColor = this.colors.success
      },
    })

    return doc.lastAutoTable.finalY + 5
  }

  private addRentSection(doc: any, autoTable: AutoTable, termData: any, rent: RentBreakdown | null, y: number, pageWidth: number): number {
    y = this.addSectionTitle(doc, 'RENTA MENSUAL VIGENTE', y, pageWidth, this.rentSourceLabel(rent))

    autoTable(doc, {
      ...this.tableBase(),
      startY: y,
      head: [['Subtotal', 'Accesorios', 'IVA', 'Total']],
      body: [[this.formatCurrency(rent?.amount), this.formatCurrency(rent?.accessories), this.formatCurrency(rent?.iva), this.formatCurrency(rent?.total)]],
      headStyles: this.headStyles(),
      columnStyles: { 0: { halign: 'center' }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 3) this.highlight(data.cell)
      },
    })

    y = doc.lastAutoTable.finalY + 3

    //* Sin accesorios financiados la tabla sería una fila de ceros: no aporta y alarga el documento.
    if (!rent || rent.accessories <= 0) return y + 2

    const detail = rent.accessoriesDetail
    const concepts = termData?.accessoriesDetail?.financed?.total?.concepts || 'Accesorios financiados'
    autoTable(doc, {
      ...this.tableBase(),
      startY: y,
      head: [['Accesorios financiados', 'Capital', 'Interés', 'IVA', 'Total']],
      body: [[concepts, this.formatCurrency(detail.capital), this.formatCurrency(detail.interest), this.formatCurrency(detail.iva), this.formatCurrency(detail.total)]],
      headStyles: this.headStyles(),
      columnStyles: {
        0: { cellWidth: (pageWidth - MARGIN * 2) * 0.32, textColor: this.colors.grey },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center', fontStyle: 'bold' },
      },
    })

    return doc.lastAutoTable.finalY + 5
  }

  private addBillingSection(doc: any, autoTable: AutoTable, leaseData: any, table: AmortizationSummary, y: number, pageWidth: number): number {
    const rents = leaseData.rents || {}
    const wallet = leaseData.wallet || {}
    const mora = leaseData.mora || {}

    y = this.addSectionTitle(doc, 'RESUMEN DE COBRANZA', y, pageWidth)

    const body: RowInput[] = [
      ['Rentas por cobrar', (rents.rentsToCollect ?? 0).toString(), this.formatCurrency(rents.rentsToCollectAmount)],
      ['Rentas cobradas', table.collected.count.toString(), this.formatCurrency(table.collected.amount)],
      //* Las anticipadas se cobran en el pago inicial, no por letra: aparte, para que no parezcan cobradas en $0.
      ...(table.anticipated.count > 0 ? [['Rentas anticipadas (pago inicial)', table.anticipated.count.toString(), this.formatCurrency(table.anticipated.amount)]] : []),
      ['Atraso (mora)', `${mora.moraDays ?? 0} días`, this.formatCurrency(mora.moraAmount)],
      ['Valor de cartera sin accesorios', '', this.formatCurrency(wallet.walletValueWithoutAccessories)],
      ['VALOR DE CARTERA', '', this.formatCurrency(wallet.walletValue)],
    ]

    const moraRow = body.length - 3
    const width = pageWidth - MARGIN * 2
    autoTable(doc, {
      ...this.tableBase(),
      startY: y,
      head: [['Concepto', 'Rentas / días', 'Monto']],
      body,
      headStyles: this.headStyles(),
      columnStyles: { 0: { cellWidth: width * 0.5, textColor: this.colors.grey }, 1: { cellWidth: width * 0.2, halign: 'center' }, 2: { cellWidth: width * 0.3, halign: 'right', fontStyle: 'bold' } },
      didParseCell: (data: any) => {
        if (data.section !== 'body') return
        if (data.row.index === body.length - 1) {
          this.highlight(data.cell)
          if (data.column.index === 0) data.cell.styles.textColor = this.colors.primary
        }
        //* La mora en rojo solo cuando existe: un cero en rojo asusta sin motivo.
        if (data.row.index === moraRow && data.column.index === 2 && mora.moraAmount > 0) data.cell.styles.textColor = this.colors.danger
      },
    })

    return doc.lastAutoTable.finalY + 6
  }

  private addNotes(doc: any, rent: RentBreakdown | null, y: number): void {
    const notes = [
      rent?.source === 'quotation'
        ? '• El arrendamiento aún no tiene tabla de amortización: la renta mostrada es la cotizada.'
        : '• La renta vigente es la de la letra indicada en la tabla de amortización; puede cambiar entre meses.',
      '• Montos en pesos mexicanos. El total de la renta incluye IVA.',
      '• Los saldos reflejan los pagos aplicados a la fecha de corte.',
    ]
    const pageHeight = doc.internal.pageSize.getHeight()
    const needed = 6 + notes.length * 3.5
    if (y + needed > pageHeight - FOOTER_RESERVED) {
      doc.addPage()
      y = CONTINUATION_HEADER_HEIGHT + 6
    }

    doc.setFontSize(6.5)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(this.colors.primary)
    doc.text('Notas importantes:', MARGIN, y)

    y += 4
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(this.colors.grey)
    notes.forEach((note) => {
      doc.text(note, MARGIN, y)
      y += 3.5
    })
  }

  // ---------- Pie ----------

  private decoratePages(doc: any, logoBase64: string, folio: string, cutoff: Date, pageWidth: number): void {
    const pageHeight = doc.internal.pageSize.getHeight()
    const total = doc.getNumberOfPages()
    const generated = `Generado el ${cutoff.toLocaleDateString('es-MX')} ${cutoff.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`

    for (let page = 1; page <= total; page++) {
      doc.setPage(page)
      if (page > 1) this.addContinuationHeader(doc, logoBase64, folio, pageWidth)

      doc.setDrawColor(this.colors.border)
      doc.setLineWidth(0.3)
      doc.line(MARGIN, pageHeight - 14, pageWidth - MARGIN, pageHeight - 14)

      doc.setFontSize(6.5)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(this.colors.grey)
      doc.text(generated, MARGIN, pageHeight - 10)
      doc.text(`Página ${page} de ${total}`, pageWidth - MARGIN, pageHeight - 10, { align: 'right' })

      doc.setFontSize(5.5)
      doc.setTextColor(this.colors.lightGrey)
      doc.text(CONTACT_LINE, pageWidth / 2, pageHeight - 5, { align: 'center' })
    }
  }

  // ---------- Piezas comunes ----------

  /** Título de sección con la barra verde de la cotización; si ya no cabe con su tabla, pasa a la página siguiente. */
  private addSectionTitle(doc: any, title: string, y: number, pageWidth: number, rightText = ''): number {
    const pageHeight = doc.internal.pageSize.getHeight()
    //* Un título solo al pie de la página, con su tabla en la siguiente, se lee como sección vacía.
    if (y + SECTION_TITLE_HEIGHT + 20 > pageHeight - FOOTER_RESERVED) {
      doc.addPage()
      y = CONTINUATION_HEADER_HEIGHT + 6
    }

    const width = pageWidth - MARGIN * 2
    doc.setFillColor(this.colors.sectionBg)
    doc.rect(MARGIN, y, width, SECTION_TITLE_HEIGHT, 'F')
    doc.setFillColor(this.colors.success)
    doc.rect(MARGIN, y, 2, SECTION_TITLE_HEIGHT, 'F')

    doc.setFontSize(7)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(this.colors.primary)
    doc.text(title, MARGIN + 5, y + 4)

    if (rightText) {
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(this.colors.grey)
      doc.text(rightText, MARGIN + width - 3, y + 4, { align: 'right' })
    }

    return y + SECTION_TITLE_HEIGHT + 3
  }

  /** Lo que comparten todas las tablas. El margen superior solo aplica a las páginas que abre autoTable. */
  private tableBase() {
    return {
      theme: 'plain' as const,
      styles: {
        fontSize: 7,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        textColor: this.colors.primary,
        lineColor: this.colors.border,
        lineWidth: 0.05,
      },
      margin: { left: MARGIN, right: MARGIN, top: CONTINUATION_HEADER_HEIGHT + 6, bottom: FOOTER_RESERVED },
    }
  }

  private headStyles() {
    return { fillColor: this.colors.headerBg, textColor: '#ffffff', fontStyle: 'bold' as const, halign: 'center' as const, fontSize: 7.5 }
  }

  private highlight(cell: any): void {
    cell.styles.fontStyle = 'bold'
    cell.styles.fillColor = this.colors.highlightBg
    cell.styles.textColor = this.colors.success
  }

  private drawCard(doc: any, x: number, y: number, width: number, height: number): void {
    doc.setFillColor(this.colors.cardBg)
    doc.setDrawColor(this.colors.border)
    doc.setLineWidth(0.3)
    doc.roundedRect(x, y, width, height, 2, 2, 'FD')
  }

  private rentSourceLabel(rent: RentBreakdown | null): string {
    if (!rent) return ''
    if (rent.source === 'quotation') return 'Según cotización'
    const date = rent.date ? `  ·  ${this.formatDate(rent.date, true)}` : ''
    return `Letra ${rent.period}${date}`
  }

  private assetNames(leaseData: any): string {
    const assets = (leaseData.quotation?.assets || []).filter((a: any) => a && typeof a === 'object' && a.name)
    if (assets.length) return assets.map((a: any) => a.name).join(', ')
    return leaseData.asset?.name || leaseData.quotation?.assetsNames || '-'
  }

  /** Correo y teléfono pueden venir varios separados por coma: en el encabezado basta el primero. */
  private firstOf(value: string | null | undefined): string {
    const first = (value || '')
      .split(/[,\n\r]+/)
      .map((v) => v.trim())
      .find((v) => v.length > 0)
    return first || '-'
  }

  private truncateText(doc: any, text: string, maxWidth: number): string {
    if (!text) return ''
    if (doc.getTextWidth(text) <= maxWidth) return text
    let truncated = text
    while (truncated.length > 0 && doc.getTextWidth(`${truncated}...`) > maxWidth) truncated = truncated.slice(0, -1)
    return `${truncated}...`
  }

  private formatCurrency(value: any): string {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '-'
    return `$ ${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  private formatPercent(value: any): string {
    if (value === null || value === undefined || isNaN(Number(value))) return '-'
    return `${Number(value).toFixed(2)} %`
  }

  //* Las fechas guardadas son medianoche UTC: en CDMX se pintarían el día anterior sin `timeZone: 'UTC'`.
  private formatDate(date: any, stored = false): string {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric', ...(stored ? { timeZone: 'UTC' } : {}) })
  }
}
